import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import React, { useState } from "react";

import { useRandomIds } from "../../../helpers/shared";
import colors from "../../../styles/tokens/colors";
import { Container, Example, Input, InputContainer, Label } from "./styles";
import AlertIcon from "../../../assets/icons/AlertIcon";

/**
 * Componente de input de texto.
 * @param {object} props - Propriedades do componente.
 * @param {string} props.label - Rótulo do input.
 * @param {string} props.placeholder - Placeholder do input.
 * @param {string} props.value - Valor do input.
 * @param {string} props.error - Mensagem de erro a ser exibida, se houver.
 * @param {boolean} props.disabled - Indica se o input está desabilitado.
 * @param {boolean} props.isPassword - Indica se o input é do tipo senha.
 * @param {boolean} [props.large=false] - Indica se o input é grande.
 * @param {string} [props.className] - Classes CSS adicionais para o input.
 * @param {string} [props.example] - Exemplo ou instrução adicional para o input.
 * @param {string} [props.width] - Largura do input.
 * @param {boolean} [props.fill=true] - Indica se o input deve preencher a largura total do seu contêiner.
 * @param {boolean} [props.bockSpaces=false] - Indica se espaços em branco devem ser bloqueados no input.
 * @param {object} [props.register={}] - Objeto contendo informações de registro para formulários React Hook.
 * @param {...any} [props] - Outras propriedades podem ser passadas e serão encaminhadas para o elemento de input HTML.
 * @returns {JSX.Element} O componente de TextInput.
 * */
function TextInput({
  label,
  placeholder,
  value,
  error,
  disabled,
  isPassword,
  large = false,
  className,
  example,
  width,
  fill = true,
  bockSpaces = false,
  register = {},
  ...props
}) {
  const [showPassword, setShowPassword] = useState(isPassword ? false : true);
  const id = useRandomIds();
  const inputId = props.id || id("input");

  return (
    <Container
      containerClass={props.containerClass}
      onKeyDown={(event) => {
        if (!bockSpaces) return;
        if (event.code === "Space") event.preventDefault();
      }}
    >
      <Label htmlFor={inputId}>{label}</Label>
      <InputContainer
        className={[value && fill && "fill", error && "error", disabled && "disabled", className]}
        large={large}
        width={width}
      >
        <Input
          maxLength={254}
          disabled={disabled}
          placeholder={placeholder}
          type={showPassword ? "text" : "password"}
          id={inputId}
          aria-invalid={error ? true : false}
          value={value}
          {...register}
          {...props}
        />

        {isPassword && (
          <div role="button" aria-hidden="true">
            {showPassword ? (
              <VisibilityIcon onClick={() => setShowPassword(false)} aria-label="Senha visível" />
            ) : (
              <VisibilityOffIcon onClick={() => setShowPassword(true)} aria-label="Senha não visível" />
            )}
          </div>
        )}

        {error && !isPassword ? (
          <div role="button" aria-hidden="true">
            <AlertIcon style={{ color: colors.error.main }} />
          </div>
        ) : null}
      </InputContainer>
      {example && (
        <Example>
          <span className="text">{example}</span>
        </Example>
      )}
          {error && error !== ' ' &&
              <span className="error-alert">
                <AlertIcon width='16' height='16' style={{ color: colors.error.main }} />
                {error}
              </span>
          }
    </Container>
  );
}

export default TextInput;
